import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Heading, Text, useResponsive } from '@common-fe/common-fe';

import { FileImage } from '@/common/constants';
import ROUTES from '@/common/routes';
import useFileErrorPopup from '@/components/controls/DropScan/hooks/useFileErrorPopup';
import UploadFileErrorPopup from '@/components/controls/DropScan/UploadFileErrorPopup';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { useBreadcrumbs } from '@/modules/core/hooks';
import useRemoveFileQuery from '@/modules/transaction/queries/useRemoveFile.query';
import { DocumentDetailsCard } from '@/modules/UseMyMoney/components';
import { useMyMoneyNavigation } from '@/modules/UseMyMoney/hooks';
import { useGetDocumentOcrQuery } from '@/modules/UseMyMoney/queries';
import { useDocumentOcrStore } from '@/modules/UseMyMoney/stores';
import {
  ReadyToScanStep,
  USE_MY_MONEY_UPLOAD_STORE_KEY,
  UseMyMoneyStep,
} from '@/modules/UseMyMoney/UseMyMoney.types';

import PreviewFile from './components/PreviewFile/PreviewFile';
import ScanFile from './components/ScanFile/ScanFile';

const ReadyToScanStepContainer = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [step, setStep] = useState(ReadyToScanStep.PREVIEW_FILE);
  const { handleSetStep } = useMyMoneyNavigation();
  const {
    values: { documentId, files },
    handleSetValues,
  } = useFilesInDocumentStore(USE_MY_MONEY_UPLOAD_STORE_KEY);
  const { getDocumentOcr, cancelOpenaiOcr } = useGetDocumentOcrQuery();
  const { setOcrDocument, setIsOcrCompleted } = useDocumentOcrStore();
  const { removeFile } = useRemoveFileQuery();

  useBreadcrumbs(
    [
      {
        key: ROUTES.HOME,
        title: t('Home'),
        value: ROUTES.HOME,
      },
      {
        key: ROUTES.USE_MY_MONEY,
        title: t('Use My Money'),
        value: ROUTES.USE_MY_MONEY,
      },
    ],
    true
  );

  const { fileUploadError, isFileUploadErrorModalVisible, onCloseFileUploadError, onFileUploadError } =
    useFileErrorPopup();

  const handleBack = useCallback(() => {
    handleSetStep(UseMyMoneyStep.SELECT_OPTION);
  }, [handleSetStep]);

  const handleSetFiles = useCallback(
    (files: FileImage[]) => {
      handleSetValues({ files });
    },
    [handleSetValues]
  );

  const handleDeleteFile = useCallback(
    (fileId: string) => {
      handleSetValues({ files: files.filter((file) => file.id !== fileId) });
      removeFile(fileId);
    },
    [handleSetValues, removeFile, files]
  );

  const handleScan = useCallback(async () => {
    if (!documentId || !files?.length) return;
    try {
      setStep(ReadyToScanStep.SCAN_FILE);
      const documentOcr = await getDocumentOcr({ documentId });
      setOcrDocument(documentOcr);
      setIsOcrCompleted(true);
      handleSetStep(UseMyMoneyStep.SUMMARY);
    } catch {
      handleSetStep(UseMyMoneyStep.SUMMARY);
    }
  }, [handleSetStep, getDocumentOcr, setOcrDocument, setIsOcrCompleted, documentId, files]);

  const handleCancelScanning = useCallback(() => {
    cancelOpenaiOcr();
    handleSetStep(UseMyMoneyStep.SUMMARY);
  }, [cancelOpenaiOcr, handleSetStep]);

  useEffect(() => {
    return () => {
      cancelOpenaiOcr();
    };
  }, [cancelOpenaiOcr]);

  return (
    <>
      <Box data-testId="ready-to-scan-file_wrapper" height={{ min: '100%' }} flex="grow">
        <Heading
          size="regular"
          data-testId="ready-to-scan_title"
          style={{
            maxWidth: isMobile ? '60%' : '100%',
            lineHeight: 1.2,
          }}
        >
          {t('Ready to Scan')}
        </Heading>

        <Text data-testId="ready-to-scan_subtitle">
          {t(
            'Confirm your document looks correct. Our system will then pull the claim details so you don’t have to type anything.'
          )}
        </Text>

        <DocumentDetailsCard margin={{ top: 'spacing12' }} />

        <Box margin={{ top: 'spacing12' }} flex="grow">
          {step === ReadyToScanStep.PREVIEW_FILE && (
            <PreviewFile
              onScan={handleScan}
              onBack={handleBack}
              onSetFiles={handleSetFiles}
              onDeleteFile={handleDeleteFile}
              onFileUploadError={onFileUploadError}
              isMobile={isMobile}
            />
          )}
          {step === ReadyToScanStep.SCAN_FILE && (
            <ScanFile onCancelScanning={handleCancelScanning} isMobile={isMobile} />
          )}
        </Box>
      </Box>

      {isFileUploadErrorModalVisible && (
        <UploadFileErrorPopup fileError={fileUploadError} onClose={onCloseFileUploadError} />
      )}
    </>
  );
};

export default ReadyToScanStepContainer;
